/**
 * Authentication Store
 * Manages authentication state using Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { LoginResponse, PaymentType } from '@/types/api.types';
import { SESSION_KEYS } from '@/config/app.config';

interface AuthState {
  // State
  token: string | null;
  policyNumber: string | null;
  paymentType: PaymentType | null;
  expiresAt: string | null;
  isAuthenticated: boolean;

  // Actions
  login: (loginResponse: LoginResponse) => void;
  logout: () => void;
  checkTokenExpiration: () => boolean;
  initializeAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      token: null,
      policyNumber: null,
      paymentType: null,
      expiresAt: null,
      isAuthenticated: false,

      // Login - Store authentication data
      login: (loginResponse: LoginResponse) => {
        set({
          token: loginResponse.token,
          policyNumber: loginResponse.policyNumber,
          paymentType: loginResponse.paymentType,
          expiresAt: loginResponse.expiresAt,
          isAuthenticated: true,
        });
      },

      // Logout - Clear all authentication data
      logout: () => {
        set({
          token: null,
          policyNumber: null,
          paymentType: null,
          expiresAt: null,
          isAuthenticated: false,
        });
      },

      // Check if token is expired
      checkTokenExpiration: () => {
        const { expiresAt, logout } = get();

        if (!expiresAt) {
          return false;
        }

        const expirationDate = new Date(expiresAt);
        const now = new Date();

        if (now >= expirationDate) {
          // Token expired, logout
          logout();
          return false;
        }

        return true;
      },

      // Initialize auth state (check expiration on app load)
      initializeAuth: () => {
        const { checkTokenExpiration } = get();
        checkTokenExpiration();
      },
    }),
    {
      name: SESSION_KEYS.AUTH_TOKEN, // Storage key
      storage: {
        getItem: (name) => {
          const str = sessionStorage.getItem(name);
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    }
  )
);

// Selector hooks for convenience
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useToken = () => useAuthStore((state) => state.token);
export const usePolicyNumber = () => useAuthStore((state) => state.policyNumber);
export const usePaymentType = () => useAuthStore((state) => state.paymentType);
